import type { TimeWheelPropsTypes } from '@Shelly/shelly-design-system/TimeWheel/TimeWheel.types';
import type { Device } from 'src/types/Device';
import {
	get_gen_2_control_component_channel_structure,
	is_gen_2,
	isWallDisplay,
} from './interface_helpers';

import { unwrapFunctionStore, format } from 'svelte-i18n';
const $format = unwrapFunctionStore(format);

const clear_gen2_component_timer_fields = (channelData: any): void => {
	if (!channelData) return;
	delete channelData.timer_started_at;
	delete channelData.timer_duration;
	delete channelData.timer_remaining;
};

const clear_gen1_channel_timer_fields = (channelData: any): void => {
	if (!channelData) return;
	channelData.has_timer = false;
	delete channelData.timer_started;
	delete channelData.timer_duration;
	delete channelData.timer_remaining;
};

/**
 * Mutates the cached device status to clear timer fields for the device's
 * primary on/off channel (or an explicit component when provided).
 *
 * The Shelly firmware cancels any running countdown when a manual `Switch.Set`
 * (without `toggle_after`) is issued. This helper mirrors that behaviour in
 * the local cache so the timer disappears from the UI immediately, instead of
 * waiting for the next full status sync. See FE-1790.
 */
export const clear_device_timer_state = (
	status: any,
	device: Device,
	options?: { component?: string },
): void => {
	if (!status) return;

	if (options?.component) {
		clear_gen2_component_timer_fields(status[options.component]);
		return;
	}

	if (is_gen_2(device)) {
		const component = get_gen_2_control_component_channel_structure(device);
		clear_gen2_component_timer_fields(status[component]);
		return;
	}

	if (device.category === 'emeter') {
		clear_gen1_channel_timer_fields(status?.relays?.[0]);
		return;
	}

	const gen1Key = device.category.split('s_')[0] + 's';
	clear_gen1_channel_timer_fields(status?.[gen1Key]?.[device.channel]);
};

export const calculate_timer = (device, status, settings, type = undefined) => {
	if (!settings) return [];

	const now = Math.floor(Date.now() / 1000);
	const isGen2 = is_gen_2(device);

	// --- Determine channel data ---
	const channelData = isGen2
		? type
			? status[type]
			: status[get_gen_2_control_component_channel_structure(device)]
		: device.category !== 'emeter'
			? status[device.category.split('s_')[0] + 's'][device.channel]
			: status.relays[0];

	// --- Extract values ---
	const beginning = isGen2 ? channelData.timer_started_at : channelData.timer_started;
	const duration = channelData.timer_duration;

	// --- Calculate time & percentage ---
	const time = isGen2
		? Math.round(beginning + duration - now)
		: beginning - settings.tz_utc_offset + duration - now;

	const percentage_remained =
		((beginning + (isGen2 ? 0 : -settings.tz_utc_offset) + duration - now) / duration) * 100;

	const clampedTime =
		percentage_remained > 100 ? Math.round((time * 100) / percentage_remained) : Math.max(0, time);
	const clampedPercentage = Math.min(100, Math.max(0, percentage_remained));

	// --- Last actuator state ---
	const ison = isGen2 ? channelData.output : channelData.ison;

	return [now, clampedTime, clampedPercentage, ison];
};

export const formatTwoDigits = (value) => {
	return String(value).padStart(2, '0');
};

export const convertSeconds = (inputSeconds) => {
	const totalMilliseconds = inputSeconds * 1000;

	const hours = Math.floor(totalMilliseconds / 3600000);
	const minutes = Math.floor((totalMilliseconds % 3600000) / 60000);
	const seconds = Math.floor((totalMilliseconds % 60000) / 1000);
	// Round milliseconds
	const milliseconds = Math.round(totalMilliseconds % 1000);
	return { hours, minutes, seconds, milliseconds };
};

export const convertToSeconds = (hours, minutes, seconds, ms = '00') => {
	const totalSeconds =
		parseInt(hours, 10) * 3600 +
		parseInt(minutes, 10) * 60 +
		parseInt(seconds, 10) +
		parseInt(ms, 10) / 1000;

	return totalSeconds;
};

export const formatTimeLabel = (hr, min, sec, ms) => {
	let text = '';
	const hasMs = ms && ms !== '00';

	if (hr !== '00') {
		text += `${hr} ${$format('timer_text_hr')}`;
	}

	if (min !== '00') {
		text += ` ${min} ${$format('timer_text_min')}`;
	}

	if (sec !== '00' || (text.length === 0 && !hasMs)) {
		text += ` ${sec} ${$format('timer_text_sec')}`;
	}

	if (hasMs) {
		text += ` ${ms} ${$format('timer_text_ms', { default: 'ms' })}`;
	}

	return `${text}`;
};

export const getTimeWheelTexts = (): TimeWheelPropsTypes['texts'] => {
	return {
		// Title used in previous implementation. By design currently is without the title
		title: $format('settings_title_timer', {
			default: 'Timer',
		}),
		hours: $format('timer_text_hr', {
			default: 'hr',
		}),
		minutes: $format('timer_text_min', {
			default: 'min',
		}),
		seconds: $format('timer_text_sec', {
			default: 'sec',
		}),
		ms: $format('timer_text_ms', {
			default: 'ms',
		}),
	};
};

export const getTimeWheelToggleText = () => {
	return $format('timer_text_milliseconds', {
		default: 'milliseconds',
	});
};

export const getTimeWheelMsStep = (deviceType: string = undefined) => {
	return isWallDisplay(deviceType) ? 1 : 10;
};
